import { Icon } from '@ui/themed';
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  BaseEdge,
  type Connection,
  type ConnectionLineComponent,
  type ConnectionLineComponentProps,
  ControlButton,
  Controls,
  type Edge,
  EdgeLabelRenderer,
  type EdgeProps,
  getBezierPath,
  Handle,
  type IsValidConnection,
  type Node,
  type NodeProps,
  type OnConnectEnd,
  type OnEdgesChange,
  type OnNodesChange,
  Position,
  ReactFlow,
  reconnectEdge,
  useReactFlow,
  useUpdateNodeInternals,
  type XYPosition,
} from '@xyflow/react';
import {
  type CSSProperties,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type AdjacencyList,
  arrangeNodes,
  buildEdges,
  convertNode,
  extractParentChildIDs,
  getEdgeId,
  type LabelComponent,
  type LabelComponentProps,
  type LabelType,
  type NodeData,
} from './logic';

type ExpectedNodeDelta =
  | { key: 'add'; position: XYPosition }
  | { key: 'remove'; ids: Set<string> }
  | null;

type NodeComponent<
  T extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  U extends Record<string, unknown> = Record<string, never>,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> = (
  node: T2 & {
    selected: boolean;
    isRoot: boolean;
    edges: Pick<Edge<U, ET>, 'id' | 'data' | 'type'>[];
  },
) => JSX.Element | null;

export type NodeType<
  T extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  U extends Record<string, unknown> = Record<string, never>,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> =
  | {
      NodeComponent: NodeComponent<T, NT, ET, U, T2>;
      hideRootHandle?: boolean;
    }
  | NodeComponent<T, NT, ET, U, T2>;

export type HandleLabelComponent<
  T extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> = (props: { handle: string; node: T2 }) => JSX.Element | null;

type ForestProps<
  T extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  U extends Record<string, unknown> = Record<string, never>,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> = PropsWithChildren<{
  canvas_width: CSSProperties['width'];
  canvas_height: CSSProperties['height'];
  block_width: number;
  block_height: number;
  vertical?: boolean;
  nodes: T2[];
  edges: AdjacencyList<U, ET>;
  nodeTypes: Record<NT, NodeType<T, NT, ET, U, T2>>;
  edgeTypes: Record<ET, LabelType<T, U, NT, T2>>;
  // This doesn't need to handle removing the node's edges from the controller state. `onRemoveEdge`
  // is triggered with the appropriate edges.
  onRemoveNode: (id: string) => void;
  onSelectNode?: (id: string, selected: boolean) => void;
  onRemoveEdge: (parent: string, handle: string, child: string) => void;
  onSelectEdge?: (parent: string, handle: string, child: string, selected: boolean) => void;
  onConnect?: (parent: string, handle: string, child: string) => void;
  onReconnect?: (
    oldSourceID: string,
    oldSourceHandle: string,
    oldTargetID: string,
    newSourceID: string,
    newSourceHandle: string,
    newTargetID: string,
  ) => void;
  isValidConnection?: (parent: string, handle: string, child: string) => boolean;
  inHandleClass?: string;
  outHandleClass?: string;
  outHandleLabel?: HandleLabelComponent<T, NT, T2>;
  /* Callback triggered when dropping a new edge onto the empty canvas. When this is triggered, the
  forest expects the next change to the nodes to be adding a single new node. This callback
  should be "open the modal to create a new node" or something similar.*/
  onAddNodeDrop?: (parent: string, handle: string) => void;
  minimumAddNodeDistance?: number;
  connectionLineEdgeType?: ET;
  defaultEdgeType?: ET;
}>;

/* Must be used withing a ReactFlowProvider
 Node IDs must not contain `/` */
export function Forest<
  T extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  U extends Record<string, unknown> = Record<string, never>,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
>({
  nodes,
  edges,
  block_width,
  block_height,
  vertical = false,
  canvas_width,
  canvas_height,
  nodeTypes,
  edgeTypes,
  onRemoveNode,
  onSelectNode = (_id, _selected) => {},
  onRemoveEdge,
  onSelectEdge = (_parent, _child, _selected) => {},
  onConnect = (_parent, _handle, _child) => {},
  onReconnect = (_oldSrc, _oldHandle, _oldTrgt, _newSrc, _newHandle, _newTrgt) => {},
  isValidConnection,
  inHandleClass,
  outHandleClass = inHandleClass,
  outHandleLabel,
  onAddNodeDrop: addNodeOnDrop,
  minimumAddNodeDistance = 200,
  connectionLineEdgeType,
  defaultEdgeType,
  children,
}: ForestProps<T, NT, ET, U, T2>) {
  // Bind layout config to arrangeNodes
  const arrange: (n: T2[], e: AdjacencyList<U, ET>) => Node<T2>[] = useCallback(
    (n, e) => arrangeNodes(n, e, block_width, block_height, vertical),
    [block_width, block_height, vertical],
  );
  const arrangeRef = useRef(arrange);

  // The state of the nodes in the graph. Manages position & selection state that the parent does
  // not care about.
  // Kept in sync with node parameters using a useEffect.
  const [localNodes, setLocalNodes] = useState<Node<T2>[]>(() => arrange(nodes, edges));
  // The state of the edges in the graph. Manages selection state that the parent does not care about.
  const [localEdges, setLocalEdges] = useState<Edge<U, ET>[]>(() => buildEdges(edges, edgeTypes));
  // Used to distinguish between change to the nodes that are caused by the graph (drag to add,
  // delete) and changes that come down from on high. This is needed to avoid rearranging the graph
  // when adding or deleting nodes.
  const [expectedNodeDelta, setExpectedNodeDelta] = useState<ExpectedNodeDelta>(null);

  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  // Ensure that local state is updated if props are updated
  useEffect(() => {
    // Automatically arrange and zoom to fit when layout config changes.
    // arrange binds layout config so checking it hasn't changed is enough
    if (arrange !== arrangeRef.current) {
      setLocalNodes(arrange(nodes, edges));
      return;
    }

    // Regenerate local edge data, this needs to be done after we regenerate nodes so that handles
    // exist when the new edges that require them are added
    if (edgesRef.current !== edges) {
      setLocalEdges(buildEdges(edges, edgeTypes));
      edgesRef.current = edges;
    }

    // Regenerate local node data if nodes are changed
    if (nodesRef.current === nodes) {
      return;
    }
    const nodeById = nodes.reduce(
      (a, x) => {
        a[x.id] = x;
        return a;
      },
      {} as Record<string, T2>,
    );
    const prevNodeById = nodesRef.current.reduce(
      (a, x) => {
        a[x.id] = x;
        return a;
      },
      {} as Record<string, T2>,
    );

    // Update the data of the local nodes to match parent
    const updateLocalData = () => {
      setLocalNodes((local) =>
        local.map((node) => {
          const data = nodeById[node.id];
          return data !== undefined ? { ...node, data } : node;
        }),
      );
    };

    const newNodes = Object.keys(nodeById).filter((id) => !prevNodeById[id]);
    const removedNodes = Object.keys(prevNodeById).filter((id) => !nodeById[id]);

    // Change to nodes does not match anything we could be expecting
    if (newNodes.length > 1 || (newNodes.length > 0 && removedNodes.length > 0)) {
      setLocalNodes(arrange(nodes, edges));
      nodesRef.current = nodes;
      return;
    }

    // No change expected:
    if (expectedNodeDelta === null) {
      if (newNodes.length > 0 || removedNodes.length > 0) {
        setLocalNodes(arrange(nodes, edges));
      } else {
        updateLocalData();
      }
      nodesRef.current = nodes;
      return;
    }

    // Addition expected:
    if (expectedNodeDelta.key === 'add') {
      const newNode = newNodes[0] ? nodeById[newNodes[0]] : undefined;
      if (newNode) {
        setLocalNodes((local) => local.concat(convertNode(newNode, expectedNodeDelta.position)));
        updateLocalData();
      } else {
        setLocalNodes(arrange(nodes, edges));
      }
      setExpectedNodeDelta(null);
      nodesRef.current = nodes;
      return;
    }

    // Removal expected:
    if (removedNodes.every((id) => expectedNodeDelta.ids.has(id))) {
      const removedNodesSet = new Set(removedNodes);
      updateLocalData();
      setExpectedNodeDelta(
        removedNodes.length === expectedNodeDelta.ids.size
          ? null
          : {
              key: 'remove',
              ids: new Set([...expectedNodeDelta.ids].filter((id) => !removedNodesSet.has(id))),
            },
      );
    } else {
      setLocalNodes(arrange(nodes, edges));
      setExpectedNodeDelta(null);
    }
    nodesRef.current = nodes;
  }, [nodes, edges, expectedNodeDelta, edgeTypes, arrange]);

  const onNodesChange: OnNodesChange<Node<T2>> = useCallback(
    (changes) => {
      // Handle changes to node state managed inside this component
      setLocalNodes((snapshot) => applyNodeChanges(changes, snapshot));

      // Run select handler after local changes made
      changes
        .filter((change) => change.type === 'select')
        .forEach(({ id, selected }) => {
          onSelectNode(id, selected);
        });

      // Handle node removal changes:
      const removeNodeChanges = changes.flatMap((change) =>
        change.type === 'remove' ? [change.id] : [],
      );
      // Drop changes if no nodes deleted
      if (!removeNodeChanges.length) {
        return;
      }
      setExpectedNodeDelta({ key: 'remove', ids: new Set(removeNodeChanges) });
      removeNodeChanges.forEach((id) => {
        onRemoveNode(id);
      });
    },
    [onRemoveNode, onSelectNode],
  );

  const onEdgesChange: OnEdgesChange<Edge<U, ET>> = useCallback(
    (changes) => {
      setLocalEdges((snapshot) => applyEdgeChanges(changes, snapshot));

      changes
        .filter((change) => change.type !== 'add' && change.type !== 'replace')
        .forEach((change) => {
          const ids = extractParentChildIDs(change.id);
          if (ids === null) return;
          const { parent, handle, child } = ids;

          switch (change.type) {
            case 'select':
              onSelectEdge(parent, handle, child, change.selected);
              break;
            case 'remove':
              onRemoveEdge(parent, handle, child);
          }
        });
    },
    [onSelectEdge, onRemoveEdge],
  );

  // Wrap node components with handles
  const wrappedNodeTypes = useMemo(
    () =>
      Object.fromEntries(
        // Object.entries erases type information so we need to type assert
        (Object.entries(nodeTypes) as [NT, NodeType<T, NT, ET, U, T2>][]).map(([key, nt]) => [
          key,
          wrapNodeComponent(nt, outHandleLabel ?? ((_) => <></>), inHandleClass, outHandleClass),
        ]),
      ),
    [nodeTypes, outHandleLabel, inHandleClass, outHandleClass],
  );

  // Construct edge components from label components & edge config
  const wrappedEdgeTypes = useMemo(
    () =>
      Object.fromEntries(
        // Object.entries erases type information so we need to type assert
        (Object.entries(edgeTypes) as [ET, LabelType<T, U, NT, T2>][]).map(([key, et]) => [
          key,
          (props: EdgeProps<Edge<U>>) => wrapEdgeComponent(et, nodes)({ key: 'edge', ...props }),
        ]),
      ),
    [edgeTypes, nodes],
  );

  // Attach information needed to render handles to nodes
  const nodesWithHandleData: NodeWithHandleData<T, NT, ET, U, T2>[] = useMemo(() => {
    // Collect a set of all nodes pointed to by an edge
    const destinationIds = localEdges.reduce(
      (a, x) => {
        a[x.target] = null;
        return a;
      },
      {} as Record<string, null>,
    );

    // Collect set of all edges by parent ID
    const edgeMap = localEdges.reduce(
      (a, x) => {
        a[x.source] = a[x.source]?.concat(x) ?? [x];
        return a;
      },
      {} as Record<string, Edge<U, ET>[]>,
    );

    return localNodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        handleData: {
          isRoot: destinationIds[node.data.id] === undefined,
          vertical,
          edges: edgeMap[node.id] ?? [],
        },
      },
    }));
  }, [localNodes, localEdges, vertical]);

  const { fitView, screenToFlowPosition } = useReactFlow();

  const handleConnect = useCallback(
    (connection: Connection) => {
      const { source, target, sourceHandle } = connection;
      // Prevent connecting from the input handle of a node
      if (sourceHandle === null) return;

      // Optimistic update using connection edge type, will be replaced when parent state changes
      setLocalEdges((snapshot) =>
        addEdge(
          {
            ...connection,
            type: connectionLineEdgeType ?? defaultEdgeType,
          },
          snapshot,
          {
            getEdgeId: ({ source, target, sourceHandle }) =>
              getEdgeId(source, sourceHandle ?? '', target),
          },
        ),
      );
      onConnect(source, sourceHandle, target);
    },
    [onConnect, connectionLineEdgeType, defaultEdgeType],
  );

  // Trigger on drop
  const onConnectEnd: OnConnectEnd = useCallback(
    (event, connectionState) => {
      if (
        connectionState.isValid ||
        addNodeOnDrop === undefined ||
        connectionState.pointer === null
      )
        return;

      // Check against a minimum distance
      if (minimumAddNodeDistance > 0 && connectionState.from !== null) {
        const { x: x1, y: y1 } = connectionState.from;
        const { x: x2, y: y2 } = connectionState.pointer;
        const d2 = (x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2);
        if (d2 < minimumAddNodeDistance * minimumAddNodeDistance) return;
      }

      const handle = connectionState?.fromHandle?.id ?? null;
      const parent = connectionState.fromNode?.id ?? null;
      const eventCoords = 'changedTouches' in event ? event.changedTouches[0] : event;
      if (eventCoords === undefined || handle === null || parent === null) return;

      // Mark that we are expecting a new node
      setExpectedNodeDelta({
        key: 'add',
        position: screenToFlowPosition({ x: eventCoords.clientX, y: eventCoords.clientY }),
      });
      // Trigger handler (eg: open a modal)
      addNodeOnDrop(parent, handle);
    },
    [addNodeOnDrop, screenToFlowPosition, minimumAddNodeDistance],
  );

  // Trigger on re-connect
  const handleReconnect = useCallback(
    (oldEdge: Edge<U, ET>, newConnection: Connection) => {
      const oldHandle = oldEdge.sourceHandle ?? null;
      const newHandle = newConnection.sourceHandle ?? null;
      if (oldHandle === null || newHandle === null) {
        return;
      }

      const sourceChanged = oldEdge.source !== newConnection.source;
      const targetChanged = oldEdge.target !== newConnection.target;
      const handleChanged = oldHandle !== newHandle;

      if (!sourceChanged && !targetChanged && !handleChanged) {
        return;
      }

      // Optimistic update, will be overwritten when parent state changes.
      oldEdge.type = connectionLineEdgeType ?? oldEdge.type;
      setLocalEdges((snapshot) =>
        reconnectEdge(oldEdge, newConnection, snapshot, {
          getEdgeId: ({ source, target, sourceHandle }) =>
            getEdgeId(source, sourceHandle ?? '', target),
        }),
      );

      onReconnect(
        oldEdge.source,
        oldHandle,
        oldEdge.target,
        newConnection.source,
        newHandle,
        newConnection.target,
      );
    },
    [onReconnect, connectionLineEdgeType],
  );

  const connectionLine: ConnectionLineComponent<NodeWithHandleData<T, NT, ET, U, T2>> | undefined =
    useMemo(() => {
      const et = connectionLineEdgeType ?? defaultEdgeType;
      if (et === undefined) return undefined;
      return (props) =>
        wrapEdgeComponent<T, NT, ET, U, T2>(edgeTypes[et], nodes)({ key: 'connection', ...props });
    }, [connectionLineEdgeType, defaultEdgeType, edgeTypes, nodes]);

  const validateConnection: IsValidConnection<Edge<U, ET>> = useCallback(
    ({ source, target, sourceHandle }) => {
      if (typeof sourceHandle !== 'string') {
        return false;
      }
      return isValidConnection?.(source, sourceHandle, target) ?? true;
    },
    [isValidConnection],
  );

  return (
    <div style={{ width: canvas_width, height: canvas_height }}>
      <ReactFlow
        nodes={nodesWithHandleData}
        edges={localEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={wrappedNodeTypes}
        edgeTypes={wrappedEdgeTypes}
        onConnect={handleConnect}
        onReconnect={handleReconnect}
        onConnectEnd={onConnectEnd}
        nodeOrigin={[0.5, 0.5]}
        defaultEdgeOptions={{ type: defaultEdgeType }}
        connectionLineComponent={connectionLine}
        isValidConnection={validateConnection}
        fitView
      >
        <Controls showInteractive={false}>
          <ControlButton
            onClick={() => {
              setLocalNodes(arrange(nodes, edges));
              fitView();
            }}
          >
            <Icon name="mapPin" />
          </ControlButton>
        </Controls>
        {children}
      </ReactFlow>
    </div>
  );
}

type NodeWithHandleData<
  T extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  U extends Record<string, unknown> = Record<string, never>,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
> = Node<
  T2 & {
    handleData: {
      isRoot: boolean;
      vertical: boolean;
      edges: Pick<Edge<U, ET>, 'id' | 'data' | 'type'>[];
    };
  }
>;

function wrapNodeComponent<
  T extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  U extends Record<string, unknown> = Record<string, never>,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
>(
  nodeType: NodeType<T, NT, ET, U, T2>,
  OutHandleLabel: HandleLabelComponent<T, NT, T2>,
  inHandleClassName?: string,
  outHandleClassName?: string,
): (node: NodeProps<NodeWithHandleData<T, NT, ET, U, T2>>) => JSX.Element {
  return ({ id, data, selected }) => {
    const { isRoot, vertical, edges } = data.handleData;

    const { NodeComponent, hideRootHandle } =
      typeof nodeType === 'object'
        ? nodeType
        : { NodeComponent: nodeType, hideRootHandle: undefined };

    const updateNodeInternals = useUpdateNodeInternals();
    // biome-ignore lint/correctness/useExhaustiveDependencies: We programatically update the handles so need to inform ReactFlow when they change
    useEffect(() => {
      updateNodeInternals(id);
    }, [updateNodeInternals, id, isRoot, hideRootHandle, data.handles]);

    return (
      <>
        {(!isRoot || !hideRootHandle) && (
          <Handle
            type="target"
            position={vertical ? Position.Top : Position.Left}
            className={inHandleClassName}
          />
        )}
        <NodeComponent {...data} selected={selected} isRoot={isRoot} edges={edges} />
        {data.handles.map((handle, idx) => {
          const position = `${((idx + 1) / (data.handles.length + 1)) * 100}%`;
          return (
            <Handle
              key={handle}
              type="source"
              position={vertical ? Position.Bottom : Position.Right}
              id={handle}
              style={vertical ? { left: position } : { top: position }}
              className={outHandleClassName}
            >
              <OutHandleLabel handle={handle} node={data} />
            </Handle>
          );
        })}
      </>
    );
  };
}

function wrapEdgeComponent<
  T extends Record<string, unknown>,
  NT extends string = string,
  ET extends string = string,
  U extends Record<string, unknown> = Record<string, never>,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
>(
  labelType: LabelType<T, U, NT, T2>,
  nodes: T2[],
): (
  props:
    | ({ key: 'edge' } & EdgeProps<Edge<U>>)
    | ({ key: 'connection' } & ConnectionLineComponentProps<NodeWithHandleData<T, NT, ET, U, T2>>),
) => JSX.Element | null {
  return (props) => {
    const {
      sourceX,
      sourceY,
      targetX,
      targetY,
      sourcePosition,
      targetPosition,
      data,
      id,
      markerEnd,
      sourceHandleId,
      targetHandleId,
      selected,
      source: sourceId,
      target: targetId,
    } = props.key === 'edge'
      ? props
      : props.fromHandle.type === 'source'
        ? {
            sourceX: props.fromX,
            sourceY: props.fromY,
            targetX: props.toX,
            targetY: props.toY,
            sourceHandleId: props.fromHandle.id,
            targetHandleId: props.toHandle?.id,
            source: props.fromNode.id,
            target: props.toNode?.id ?? undefined,
          }
        : {
            sourceX: props.toX,
            sourceY: props.toY,
            targetX: props.fromX,
            targetY: props.fromY,
            sourceHandleId: props.toHandle?.id,
            targetHandleId: props.fromHandle.id,
            source: props.toNode?.id ?? undefined,
            target: props.fromNode.id,
          };

    if (
      typeof sourceX !== 'number' ||
      typeof sourceY !== 'number' ||
      Number.isNaN(sourceX) ||
      Number.isNaN(sourceY) ||
      Number.isNaN(targetX) ||
      Number.isNaN(targetY)
    ) {
      console.warn('RemovableEdge: Invalid coordinates', { sourceX, sourceY, targetX, targetY });
      return null;
    }

    const [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition: sourcePosition || Position.Right,
      targetX,
      targetY,
      targetPosition: targetPosition || Position.Left,
    });

    const source = nodes.find(({ id }) => id === sourceId);
    const target = nodes.find(({ id }) => id === targetId);

    const labelComponentProps: LabelComponentProps<T, U, NT, T2> = {
      id,
      sourceHandleId: sourceHandleId ?? undefined,
      targetHandleId,
      data,
      source,
      target,
    };

    const labelComponent = typeof labelType === 'object' ? labelType.labelComponent : labelType;

    const labelStyles: CSSProperties | undefined =
      typeof labelType === 'object'
        ? {
            stroke: selected ? labelType.selectedColour : labelType.colour,
            strokeWidth: labelType.width,
            opacity: labelType.opacity,
            strokeDasharray: labelType.dashed,
          }
        : undefined;

    return (
      <>
        <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={labelStyles} />
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
            }}
          >
            {labelComponent(labelComponentProps)}
          </div>
        </EdgeLabelRenderer>
      </>
    );
  };
}

export function deleteLabelButton<
  T extends Record<string, unknown>,
  U extends Record<string, unknown>,
  NT extends string = string,
  T2 extends NodeData<T, NT> = NodeData<T, NT>,
>(): LabelComponent<T, U, NT, T2> {
  return ({ id }) => {
    const { deleteElements } = useReactFlow();
    const handleRemove = useCallback(() => {
      if (id !== undefined) deleteElements({ edges: [{ id }] });
    }, [id, deleteElements]);
    return (
      <button
        className="flex size-6 cursor-pointer items-center justify-center rounded-full border border-border bg-surface-raised text-text-secondary text-xs leading-none hover:bg-error hover:text-text-inverse"
        onClick={() => handleRemove()}
        title="Remove connection"
        type="button"
      >
        ×
      </button>
    );
  };
}
